import { Client, InputHandle, Predict, Room } from "@colyseus/sdk";
import type { MapSchema } from "@colyseus/schema";

import {
    ArenaState,
    FIRE_MESSAGE,
    HelmInput,
    HIT_MESSAGE,
    JoinOptions,
    Loot,
    SHOT_MESSAGE,
    ShotFired,
    ShotHit,
    Ship
} from "../../../shared/ArenaState";
import { Loadout } from "../../../shared/Fitout";
import { arenaEndpoint } from "./Endpoint";

/** The room name registered in `server/index.ts`. */
const ROOM_NAME = "battle";

/**
 * How many times a dropped socket is retried, and the longest gap between attempts in ms.
 *
 * Sized so the whole run fits inside the arena's `RECONNECT_WINDOW`: past that the seat has gone
 * and the hull is a ghost, so a further attempt is a question whose answer cannot change. The SDK's
 * own defaults are fifteen attempts over about fifty seconds, which is forty-five seconds of a
 * player watching a still sea while being told nothing.
 */
const RECONNECT_RETRIES = 6;
const RECONNECT_RETRY_CAP_MS = 1000;

/**
 * How far the prediction's own render clock may be pushed in one frame, in fixed steps.
 *
 * `predict.tick()` counts the steps that are due against the room's clock and hands back the
 * number; on a tab returning from the background that number can be enormous, and sending a helm
 * command for every one of them would flood the arena with a rudder nobody touched. Past this the
 * surplus is dropped — the boat simply carries on from wherever the arena has it, which is what
 * reconciliation is for.
 */
const MAX_STEPS_PER_FLUSH = 5;

/**
 * The client's end of an arena. Owns the Colyseus room and the one message the client is
 * allowed to send; everything about where ships actually are comes back through
 * `ships` and is read-only. See design/Networking.md.
 */
export class BattleConnection {
    private _room: Room<any, ArenaState> | null = null;
    /**
     * The input channel: one staged `HelmInput` that is mutated in place and sent once per fixed
     * step. Null until a room is joined.
     *
     * It replaces the three rate-limited, deduplicated message streams this class used to run —
     * `steer` and `aim` at 20 Hz apiece, `turbo` on the edge — and the deduplication goes with
     * them. **A predicted client cannot be silent**: the arena numbers these and tells us which it
     * has acted on, and a step we skipped is a step the replay has no command for. Colyseus sends a
     * body-less frame when nothing changed, which is what keeps that affordable.
     */
    private _input: InputHandle<HelmInput> | null = null;
    /**
     * The room's prediction engine: the fixed-step clock everything on this side is paced by, and
     * what a reconciler is built on. Null until a room is joined.
     *
     * **It owns the pacing now, and that replaced two hand-rolled attempts at it.** Counting steps
     * off local elapsed time drifts against the arena and quietly builds a backlog that never
     * drains — measured as reported round trip climbing over a session. `tick()` counts against
     * the room clock instead, which is synchronised to the server's, so the client cannot outrun
     * the arena however fast it renders. See design/Networking.md.
     */
    private _predict: Predict | null = null;
    /** What the player is currently asking for. Staged by the setters, sent by `flush`. */
    private _heading = 0;
    private _aim = 0;
    private _turboHeld = false;
    /**
     * Set while this client is the one ending the connection, so `onLeave` can tell a departure
     * it asked for from one that happened to it. Both arrive down the same signal.
     *
     * A flag rather than reading the close code, because the two are different questions and only
     * one of them is ours to answer. `leave()` also drops its listeners, so in practice a
     * consented close never reaches the handler — this is what makes that a belt rather than the
     * only thing holding the trousers up.
     */
    private _leaving = false;

    /**
     * A gun went off, anywhere in the sea — the client's own included. Broadcast rather than
     * state, so this is the only time anybody hears about a ball; whoever is drawing them flies
     * it from here. See `SHOT_MESSAGE` in shared/ArenaState.ts.
     */
    public onShot: ((shot: ShotFired) => void) | null = null;
    /** A ball found a hull. Ends the flight it names, and says what the hit was worth. */
    public onHit: ((hit: ShotHit) => void) | null = null;
    /**
     * The arena stopped answering, and this client did not ask it to — a dropped socket, a server
     * that went away, a room that errored.
     *
     * It matters because **nothing else can tell.** Colyseus state is a stream of patches, and a
     * stream that has stopped looks exactly like a sea where nothing is happening: without this
     * the tab keeps rendering the last frame it decoded, the player keeps steering a still world,
     * and there is no way to distinguish a dead connection from a quiet one. See
     * design/Networking.md.
     *
     * The ship the player was sailing is not lost with the socket — the arena keeps it as a ghost
     * (design/Battle.md) — but this client has no claim on it any more, and no way back to it.
     */
    public onLost: ((detail: string) => void) | null = null;

    /** This client's key into `ships`, or `""` before the room is joined. */
    public get sessionId(): string {
        return this._room?.sessionId ?? "";
    }

    public get connected(): boolean {
        return this._room !== null;
    }

    /** Every ship in the sea, keyed by session id. Null until the first state arrives. */
    public get ships(): MapSchema<Ship> | null {
        return this._room?.state?.ships ?? null;
    }

    /** Every floating pickup, keyed by the server's loot id. Null until the first state arrives. */
    public get loot(): MapSchema<Loot> | null {
        return this._room?.state?.loot ?? null;
    }

    /**
     * Which way the wind is blowing, in radians, 0 = north — the direction it blows **toward**,
     * per `shared/Wind.ts`. North before the first patch, which is a frame or two of a vane
     * pointing up rather than a wrong reading anybody can act on: nothing in the game reads the
     * wind yet, and the sea is not drawn until there is a ship in it.
     */
    public get wind(): number {
        return this._room?.state?.wind ?? 0;
    }

    /**
     * The round trip to the arena and its jitter, in milliseconds — or **null when there is no
     * reading yet**, which is a real state rather than a placeholder.
     *
     * Colyseus derives both by correlating the input sequence the arena echoes back against this
     * client's own send times, so a figure only exists once helm packets have been going out for
     * a moment. Before that, and on a room that declared no input at all, the SDK hands back a
     * stub clock reading zero — which is why zero is reported as "no reading" rather than as a
     * perfect connection. See design/Networking.md.
     */
    public get latency(): { rtt: number; jitter: number } | null {
        const rtt = this._room?.clock.smoothedRtt() ?? 0;
        if (rtt <= 0) return null;

        return { rtt, jitter: this._room?.clock.jitter() ?? 0 };
    }

    /** Joins the arena, creating it if this is the first boat in. */
    public async join(name: string, loadout: Loadout): Promise<void> {
        const client = new Client(arenaEndpoint());
        // Passing the schema explicitly means the decoder builds our own `ArenaState`
        // rather than a reflected stand-in, so `ships` is typed all the way through.
        //
        // Both halves of the payload are **requests**: the arena trims the name and runs the
        // loadout through `sanitiseLoadout`, and neither is checked for entitlement — it cannot
        // ask whether the boat was paid for. See design/Networking.md and design/Shop.md.
        const options: JoinOptions = {
            name,
            loadout: {
                preset: loadout.preset,
                cannons: [...loadout.cannons],
                sailTint: loadout.sailTint,
                hullTint: loadout.hullTint,
                emblem: loadout.emblem,
                emblemTint: loadout.emblemTint
            }
        };
        this._room = await client.joinOrCreate<ArenaState>(ROOM_NAME, options, ArenaState);

        // The helm channel. The class is passed explicitly for the same reason `ArenaState` is —
        // the SDK would otherwise synthesise one from the server's reflection, and this way the
        // staged fields are the ones `shared/ArenaState.ts` declares, checked at compile time.
        // Reliable rather than unreliable: rollback wants redundancy across drops, and
        // design/Networking.md is where that trade is recorded, with a measurement.
        this._input = this._room.input<HelmInput>({ type: HelmInput });

        // The prediction engine. `raw` because nothing is passively attached to it yet — remote
        // hulls are still eased by hand, which was measured against the SDK's own interpolation
        // and kept (design/Networking.md, known rough edges) — but the fixed-step
        // clock and the rollback machinery it drives are wanted from the first frame.
        this._predict = Predict.get(this._room, { mode: "raw" });

        // **On**, and sized against the window the arena actually holds a seat open for.
        //
        // It was off for as long as there was nothing to reconnect *to*: an unconsented drop turned
        // the hull into a ghost immediately and let the seat go, so every retry was guaranteed to
        // fail and a player whose connection died sat in front of a frozen sea for the better part
        // of a minute before being told anything. The arena holds the seat for RECONNECT_WINDOW now
        // (design/Battle.md), so the retries can succeed and are worth the wait.
        //
        // The budget is cut to fit inside that window rather than left at the SDK's fifteen
        // attempts over ~50s: retrying past the point where the seat has already gone is asking a
        // question whose answer cannot change, and every second of it is a second the player is
        // watching a still sea being told nothing. At this backoff — 100ms doubling, capped at
        // RECONNECT_RETRY_CAP_MS — six attempts land inside five seconds.
        this._room.reconnection.enabled = true;
        this._room.reconnection.maxRetries = RECONNECT_RETRIES;
        this._room.reconnection.maxDelay = RECONNECT_RETRY_CAP_MS;

        // Registered here rather than by the scene, so a listener cannot be attached to a room
        // that is about to be replaced — `leave` drops both the room and these with it.
        this._room.onMessage(SHOT_MESSAGE, (shot: ShotFired) => this.onShot?.(shot));
        this._room.onMessage(HIT_MESSAGE, (hit: ShotHit) => this.onHit?.(hit));

        // **The one signal that means the arena is gone**, and the only one acted on. Consented
        // closes come through here too — the Leave button, and the scene tearing itself down after
        // a sinking — and those are not losses, so they are filtered rather than reported.
        //
        // With reconnection on, the SDK invokes this exactly when coming back has been ruled out:
        // retries disabled, a room too young to retry against, or a retry budget actually spent.
        // A dropped socket that heals inside the window never reaches here at all, which is the
        // whole point — a player should not be told about a blip they did not notice.
        this._room.onLeave((code, reason) => {
            if (this._leaving) return;
            this._lost(`closed with code ${code}${reason ? `: ${reason}` : ""}`);
        });

        // **An error is not a loss any more, and this is the line that most needed changing when
        // reconnection was turned on.** It used to report one — "distinct from a close in Colyseus,
        // identical in consequence here" — which was true while a dropped socket was final. It is
        // not now: the transport raises an error *and then* closes, and the close is what starts
        // the retries. Reporting the error would announce the connection lost a few hundred
        // milliseconds before the client had even begun trying to get it back, and the player
        // would be put ashore from a blip that was about to heal.
        //
        // `onLeave` is the single authoritative signal, and the SDK is careful about it: it fires
        // only when reconnection is off, when the room has not been up long enough to retry, or
        // when the retries have actually run out. So this is a note in the log and nothing more.
        this._room.onError((code, message) => console.warn(`[battle] arena error ${code}${message ? `: ${message}` : ""}`));
    }

    /**
     * Reports the connection as gone, once. Latched with `_leaving` rather than a second flag:
     * from this point on the client *is* leaving, so a close arriving after an error — which is
     * the normal order — does not report the same loss twice.
     */
    private _lost(detail: string): void {
        if (this._leaving) return;

        this._leaving = true;
        this.onLost?.(detail);
    }

    /**
     * Asks for a gun to be fired, by its index in the roster `shared/Gunnery.ts` builds. A
     * request like the other two: the server decides whether that gun has reloaded, where its
     * muzzle is and which way it points, and answers — if it answers — with a broadcast shot.
     *
     * Sent when the scene picks a gun, with no rate limit of its own: the reload is the rate
     * limit, and the scene keeps its own copy of every gun's clock so it never names one that is
     * still loading. A held trigger sends one of these per gun per reload — see
     * design/Controls.md — and a request the server declines costs one packet and nothing else.
     */
    public fire(index: number): void {
        this._room?.send(FIRE_MESSAGE, index);
    }

    /**
     * Asks the server for a course, in radians. **Staged, not sent** — `flush` puts it on the wire
     * once per fixed step, along with the aim and the turbo, because the arena consumes one
     * command per step and a replay needs to know which step each belonged to.
     */
    public steer(heading: number): void {
        this._heading = heading;
    }

    /**
     * Asks the server to train the guns on a direction. Staged like the course.
     *
     * A **direction and nothing else.** How far a gun actually gets round is the server's, out
     * of the arc and the traverse rate it already owns — see design/Controls.md.
     */
    public aim(heading: number): void {
        this._aim = heading;
    }

    /**
     * Says whether the turbo button is down. A **level rather than an edge** now: it used to be
     * sent only when it changed, which is cheaper and cannot be replayed — an edge landing inside
     * a rollback window has no defined position in the sequence, whereas a level is simply true
     * for every step it was held.
     */
    public turbo(held: boolean): void {
        this._turboHeld = held;
    }

    /**
     * Advances the prediction clock and puts the staged helm on the wire, once per fixed step the
     * arena is owed. **Call this exactly once a frame, before anything reads the prediction** —
     * it is what steps the reconciler, replays unacknowledged inputs on top of the newest server
     * answer, and decays the correction.
     *
     * Nothing here is deduplicated: silence is a hole in the replay, so a still pointer sends the
     * same command again rather than nothing. Colyseus sends a body-less frame when nothing has
     * changed, which is what keeps that affordable.
     */
    public flush(): void {
        const input = this._input;
        const predict = this._predict;
        if (!input || !predict) return;

        const due = Math.min(predict.tick(), MAX_STEPS_PER_FLUSH);
        for (let i = 0; i < due; i++) {
            input.data.heading = this._heading;
            input.data.aim = this._aim;
            input.data.turbo = this._turboHeld;
            input.send();
        }
    }

    /**
     * The room's prediction engine, for whoever is building a reconciler on it — the scene, which
     * owns what the player's own hull is predicted to be doing. Null before a room is joined.
     */
    public get predict(): Predict | null {
        return this._predict;
    }

    /** The helm channel a reconciler observes for sends and acknowledgements. Null before joining. */
    public get input(): InputHandle<HelmInput> | null {
        return this._input;
    }

    public leave(): void {
        // Set first: `leave(true)` can settle the socket before the next line runs, and a close
        // handler that fired in between would report the player's own departure as a loss.
        this._leaving = true;
        // Consented, so the server drops the seat immediately instead of holding it open
        // for a reconnection that is not coming.
        this._room?.leave(true);
        this._room?.removeAllListeners();
        this._room = null;
        this._input = null;
        this._predict = null;
        this._leaving = false;
        // Forgotten with the room. The staged helm goes with it so the next arena is not steered
        // by the last sea's rudder for the frame before the player touches anything.
        this._turboHeld = false;
        this._heading = 0;
        this._aim = 0;
    }
}
